import { Alert } from 'antd'
import { BillList, GroupLedger } from '@/features/bill/index.ts'
import { orderListSearchSchema, RentalOrderList, ServiceOrderList } from '@/features/order/index.ts'
import { useAccess } from '@/hooks/use-access.ts'
import { getCustomerDocumentsDefaults } from './search.ts'
import type { CustomerDocumentsViewProps } from './types.ts'

export function CustomerDocumentsContent({
  customerId,
  currentSession,
  search,
  onSearchChange
}: CustomerDocumentsViewProps) {
  const access = useAccess()
  const permissions = {
    rental: 'order.rental.read',
    service: 'order.service.read',
    bills: 'bill.read',
    groups: 'bill.group.read'
  } as const
  if (!access.can(permissions[search.kind]))
    return <Alert type="info" showIcon title="你没有查看此类单据的权限，请选择已授权的分类。" />
  const defaults = getCustomerDocumentsDefaults(customerId)
  const canManageBills =
    currentSession.isSuperAdmin ||
    currentSession.permissions.some((permission) => permission.startsWith('bill.') && !permission.endsWith('.read'))
  return (
    <main className="bg-surface min-w-0 overflow-hidden rounded-xl">
      {search.kind === 'rental' && (
        <RentalOrderList
          key={`rental-${customerId}`}
          compact
          customerScoped
          currentSession={currentSession}
          search={search.rental}
          defaultSearch={defaults.rental}
          navigateSearch={(options) =>
            onSearchChange(
              { rental: typeof options.search === 'function' ? options.search(search.rental) : options.search },
              options.replace
            )
          }
        />
      )}
      {search.kind === 'service' && (
        <ServiceOrderList
          key={`service-${customerId}`}
          compact
          customerScoped
          currentSession={currentSession}
          search={search.service}
          defaultSearch={defaults.service}
          navigateSearch={(options) =>
            onSearchChange(
              {
                service: orderListSearchSchema.parse(
                  typeof options.search === 'function' ? options.search(search.service) : options.search
                )
              },
              options.replace
            )
          }
        />
      )}
      {search.kind === 'bills' && (
        <BillList
          key={`bills-${customerId}`}
          customerScoped
          currentSession={currentSession}
          search={search.bills}
          defaultSearch={defaults.bills}
          navigateSearch={(options) =>
            onSearchChange(
              { bills: typeof options.search === 'function' ? options.search(search.bills) : options.search },
              options.replace
            )
          }
        />
      )}
      {search.kind === 'groups' && (
        <GroupLedger
          key={`groups-${customerId}`}
          customerScoped
          canManage={canManageBills}
          search={search.groups}
          defaultSearch={defaults.groups}
          setSearch={(groups) => void onSearchChange({ groups })}
        />
      )}
    </main>
  )
}
